import { ChartEvent } from '../chart/types';
import { apiGet, apiPost, apiDelete, apiPut, toApiUrl } from '../../../services/api';
import { getToken } from '../../../services/auth';

type SaveChartEventResult = {
  persistedId?: string | number;
  visitVersion?: number;
};

type DeleteChartEventResult = {
  visitVersion?: number;
};

export type CareflowChartBundle = {
  events: ChartEvent[];
  visitVersion: number;
  streamUpdatedAt: string | null;
};

export type CareflowAuditEntry = {
  id: number;
  visit_id: number;
  patient_id?: number | null;
  diagnosis_id?: number | null;
  action: string;
  tooth_number?: string | null;
  diagnosis?: string | null;
  note?: string | null;
  event_type?: string | null;
  source?: string | null;
  created_at?: string | null;
};

export type CareflowVisitTagsResponse = {
  visit_id: number;
  tags: string[];
  updated_at?: string | null;
};

export type CareflowToothHistoryPayload = {
  id: number;
  history_id: number;
  visit_id: number;
  patient_id?: number | null;
  tooth_number?: string | null;
  tooth_id?: number | null;
  label: string;
  note?: string;
  created_at?: string | null;
  timestamp?: string | null;
};

export type CareflowCollaborationNote = {
  id: number;
  visit_id: number;
  patient_id?: number | null;
  author_user_id?: number | null;
  author_role?: string | null;
  body: string;
  mentions: string[];
  attachment_ids: number[];
  note_version: number;
  created_at?: string | null;
  updated_at?: string | null;
};

export type CareflowCollaborationAuditEntry = {
  id: number;
  visit_id: number;
  note_id?: number | null;
  action: string;
  actor_user_id?: number | null;
  actor_role?: string | null;
  payload?: Record<string, unknown> | null;
  created_at?: string | null;
};

export type CareflowCollaborationBundle = {
  visit_id: number;
  notes: CareflowCollaborationNote[];
  audit: CareflowCollaborationAuditEntry[];
};

export type CareflowLiveEvent = {
  event: string;
  visit_id: number;
  ts?: string;
  stream_version?: number;
  stream_updated_at?: string | null;
  [key: string]: unknown;
};

type CareflowLiveStatus = 'connecting' | 'open' | 'closed' | 'error' | 'unauthenticated';

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function toTimestamp(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return Date.now();
}

function normalizeType(value: unknown): ChartEvent['type'] {
  if (value === 'state' || value === 'treatment' || value === 'both') return value;
  return 'both';
}

function normalizeDomain(value: unknown, fallback: ChartEvent['domain']): ChartEvent['domain'] {
  return value === 'history' || value === 'treatment' ? value : fallback;
}

function normalizeEvent(
  raw: Record<string, unknown>,
  defaultVisitId: number,
  isHistorical: boolean,
  fallbackDomain: ChartEvent['domain'] = 'treatment'
): ChartEvent | null {
  const toothId = Number(
    raw.toothId ?? raw.tooth_id ?? raw.tooth ?? raw.tooth_number ?? raw.tooth_no
  );
  if (!Number.isFinite(toothId) || toothId <= 0) return null;

  const persistedId =
    (raw.persistedId as string | number | undefined) ??
    (raw.event_id as string | number | undefined) ??
    (raw.history_id as string | number | undefined) ??
    (raw.id as string | number | undefined);

  const idSource = raw.id ?? raw.event_id ?? raw.history_id ?? persistedId;
  const id =
    idSource != null && String(idSource).trim().length > 0
      ? String(idSource)
      : crypto.randomUUID();

  const label = String(raw.label ?? raw.diagnosis ?? raw.description ?? 'other').trim();
  if (!label) return null;

  const domain = normalizeDomain(raw.domain, fallbackDomain);

  return {
    id,
    persistedId,
    visitId: Number(raw.visitId ?? raw.visit_id ?? defaultVisitId) || defaultVisitId,
    isHistorical: Boolean(raw.isHistorical ?? isHistorical),
    toothId,
    label,
    note: String(raw.note ?? raw.notes ?? ''),
    timestamp: toTimestamp(raw.timestamp ?? raw.created_at),
    type: normalizeType(raw.type),
    domain,
    columnId: String(raw.columnId ?? raw.column_id ?? raw.status ?? '').trim() || undefined,
  };
}

function dedupeChartEvents(events: ChartEvent[]): ChartEvent[] {
  const seen = new Map<string, ChartEvent>();

  for (const event of events) {
    const persisted =
      event.persistedId != null && String(event.persistedId).trim().length > 0
        ? `persisted:${String(event.persistedId)}`
        : null;
    const synthetic = [
      `domain:${event.domain || 'treatment'}`,
      `visit:${event.visitId ?? 0}`,
      `tooth:${event.toothId}`,
      `label:${String(event.label || '').toLowerCase()}`,
      `time:${Math.floor((event.timestamp || 0) / 1000)}`,
      `hist:${event.isHistorical ? 1 : 0}`,
    ].join('|');
    const key = persisted || synthetic;

    const existing = seen.get(key);
    if (!existing || (event.timestamp || 0) >= (existing.timestamp || 0)) {
      seen.set(key, event);
    }
  }

  return Array.from(seen.values()).sort((a, b) => a.timestamp - b.timestamp);
}

function parseVisitVersion(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

function toCollaborationNote(raw: unknown): CareflowCollaborationNote | null {
  const r = asRecord(raw);
  if (!r) return null;
  const id = Number(r.id);
  const visitId = Number(r.visit_id);
  if (!Number.isFinite(id) || id <= 0 || !Number.isFinite(visitId) || visitId <= 0) return null;
  const mentions = Array.isArray(r.mentions)
    ? r.mentions.map((item) => String(item || '').trim()).filter((item) => item.length > 0)
    : [];
  const attachmentIds = Array.isArray(r.attachment_ids)
    ? r.attachment_ids
        .map((item) => Number(item))
        .filter((item) => Number.isFinite(item) && item > 0)
        .map((item) => Number(item))
    : [];
  return {
    id,
    visit_id: visitId,
    patient_id:
      r.patient_id == null
        ? null
        : Number.isFinite(Number(r.patient_id))
        ? Number(r.patient_id)
        : null,
    author_user_id:
      r.author_user_id == null
        ? null
        : Number.isFinite(Number(r.author_user_id))
        ? Number(r.author_user_id)
        : null,
    author_role: r.author_role == null ? null : String(r.author_role),
    body: String(r.body ?? ''),
    mentions,
    attachment_ids: attachmentIds,
    note_version: Number.isFinite(Number(r.note_version)) ? Number(r.note_version) : 1,
    created_at: r.created_at == null ? null : String(r.created_at),
    updated_at: r.updated_at == null ? null : String(r.updated_at),
  };
}

function toCollaborationAudit(raw: unknown): CareflowCollaborationAuditEntry | null {
  const r = asRecord(raw);
  if (!r) return null;
  const id = Number(r.id);
  const visitId = Number(r.visit_id);
  if (!Number.isFinite(id) || id <= 0 || !Number.isFinite(visitId) || visitId <= 0) return null;
  return {
    id,
    visit_id: visitId,
    note_id:
      r.note_id == null ? null : Number.isFinite(Number(r.note_id)) ? Number(r.note_id) : null,
    action: String(r.action ?? 'updated'),
    actor_user_id:
      r.actor_user_id == null
        ? null
        : Number.isFinite(Number(r.actor_user_id))
        ? Number(r.actor_user_id)
        : null,
    actor_role: r.actor_role == null ? null : String(r.actor_role),
    payload: asRecord(r.payload),
    created_at: r.created_at == null ? null : String(r.created_at),
  };
}

export const careflow360Api = {
  async fetchChartEvents(visitId: number, patientId?: string): Promise<CareflowChartBundle> {
    const combined: ChartEvent[] = [];
    let visitVersion = 0;
    let streamUpdatedAt: string | null = null;
    let loadedFromNewEndpoint = false;

    try {
      const response = (await apiGet(
        `/api/doctor/careflow360/events/${visitId}?with_meta=1`
      )) as unknown;
      if (Array.isArray(response)) {
        const mapped = response
          .map((event) => normalizeEvent(event as Record<string, unknown>, visitId, false))
          .filter((event): event is ChartEvent => event !== null);
        if (mapped.length > 0) {
          loadedFromNewEndpoint = true;
          combined.push(...mapped);
        }
      } else {
        const meta = asRecord(response);
        if (meta) {
          const currentEvents = Array.isArray(meta.events) ? meta.events : [];
          const currentHistory = Array.isArray(meta.tooth_history) ? meta.tooth_history : [];
          const mappedEvents = currentEvents
            .map((event) =>
              normalizeEvent(event as Record<string, unknown>, visitId, false, 'treatment')
            )
            .filter((event): event is ChartEvent => event !== null);
          const mappedHistory = currentHistory
            .map((event) =>
              normalizeEvent(event as Record<string, unknown>, visitId, false, 'history')
            )
            .filter((event): event is ChartEvent => event !== null);
          combined.push(...mappedEvents, ...mappedHistory);
          visitVersion = parseVisitVersion(meta.stream_version);
          streamUpdatedAt =
            meta.stream_updated_at == null ? null : String(meta.stream_updated_at || '');
          loadedFromNewEndpoint = mappedEvents.length > 0 || mappedHistory.length > 0;
        }
      }
    } catch {
      // Fallback is handled below.
    }

    if (!loadedFromNewEndpoint) {
      try {
        const legacyCurrent = (await apiGet(`/api/doctor/diagnoses/${visitId}`)) as unknown;
        if (Array.isArray(legacyCurrent)) {
          const mappedCurrent = legacyCurrent
            .map((diag) => normalizeEvent(diag as Record<string, unknown>, visitId, false))
            .filter((event): event is ChartEvent => event !== null);
          combined.push(...mappedCurrent);
        }
      } catch (e) {
        console.error('Failed to fetch legacy diagnoses for current visit', e);
      }
    }

    if (patientId) {
      try {
        const visitsRes = (await apiGet(
          `/api/patients/${encodeURIComponent(patientId)}/visits`
        )) as unknown;
        if (Array.isArray(visitsRes)) {
          const historicalVisitIds = visitsRes
            .map((visit) => Number((visit as { visit_id?: unknown }).visit_id))
            .filter((id) => Number.isFinite(id) && id > 0 && id !== visitId)
            .slice(0, 10);

          if (historicalVisitIds.length > 0) {
            const historicalResponses = await Promise.all(
              historicalVisitIds.map(async (historicalVisitId) => {
                try {
                  const response = (await apiGet(
                    `/api/doctor/careflow360/events/${historicalVisitId}?with_meta=1`
                  )) as unknown;
                  return { historicalVisitId, response };
                } catch {
                  return { historicalVisitId, response: [] as unknown[] };
                }
              })
            );

            for (const { historicalVisitId, response } of historicalResponses) {
              if (Array.isArray(response)) {
                const mapped = response
                  .map((event) =>
                    normalizeEvent(event as Record<string, unknown>, historicalVisitId, true)
                  )
                  .filter((event): event is ChartEvent => event !== null);
                combined.push(...mapped);
                continue;
              }

              const meta = asRecord(response);
              if (meta) {
                const historyEvents = Array.isArray(meta.events) ? meta.events : [];
                const historyToothEvents = Array.isArray(meta.tooth_history)
                  ? meta.tooth_history
                  : [];
                combined.push(
                  ...historyEvents
                    .map((event) =>
                      normalizeEvent(
                        event as Record<string, unknown>,
                        historicalVisitId,
                        true,
                        'treatment'
                      )
                    )
                    .filter((event): event is ChartEvent => event !== null),
                  ...historyToothEvents
                    .map((event) =>
                      normalizeEvent(
                        event as Record<string, unknown>,
                        historicalVisitId,
                        true,
                        'history'
                      )
                    )
                    .filter((event): event is ChartEvent => event !== null)
                );
                continue;
              }

              try {
                const legacy = (await apiGet(
                  `/api/doctor/diagnoses/${historicalVisitId}`
                )) as unknown;
                if (Array.isArray(legacy)) {
                  combined.push(
                    ...legacy
                      .map((diag) =>
                        normalizeEvent(diag as Record<string, unknown>, historicalVisitId, true)
                      )
                      .filter((event): event is ChartEvent => event !== null)
                  );
                }
              } catch {
                // Ignore per-visit fallback failures.
              }
            }
          }
        }
      } catch (e) {
        console.warn('Failed to fetch historical charting for patient', e);
      }
    }

    return {
      events: dedupeChartEvents(combined),
      visitVersion,
      streamUpdatedAt,
    };
  },

  async saveChartEvent(
    visitId: number,
    patientId: string,
    event: ChartEvent,
    expectedVersion?: number | null
  ): Promise<SaveChartEventResult> {
    let success = false;
    let wroteNewEndpoint = false;
    let persistedId: string | number | undefined;
    let visitVersion: number | undefined;

    try {
      const newResponse = (await apiPost(`/api/doctor/careflow360/events/${visitId}`, {
        patient_id: patientId,
        ...event,
        expected_version: expectedVersion ?? undefined,
        client_event_id: event.id,
        column_id: event.columnId,
      })) as Record<string, unknown> | null;
      if (newResponse && typeof newResponse === 'object') {
        persistedId =
          (newResponse.id as string | number | undefined) ??
          (newResponse.event_id as string | number | undefined) ??
          persistedId;
        visitVersion = parseVisitVersion(newResponse.stream_version);
      }
      success = true;
      wroteNewEndpoint = true;
    } catch (error) {
      const e = error as Error & { status?: number };
      if (e.status === 409) throw error;
      console.warn('New careflow360 endpoint failed, writing legacy');
    }

    if (!wroteNewEndpoint) {
      try {
        const legacyResponse = (await apiPost(`/api/doctor/diagnoses`, {
          visit_id: visitId,
          patient_id: patientId,
          tooth_number: String(event.toothId),
          diagnosis: event.label,
          notes: event.note || '',
        })) as Record<string, unknown> | null;

        if (legacyResponse && typeof legacyResponse === 'object') {
          persistedId =
            (legacyResponse.id as string | number | undefined) ??
            (legacyResponse.diagnosis_id as string | number | undefined) ??
            persistedId;
        }
        success = true;
      } catch (e) {
        console.error('Legacy diagnose save failed', e);
      }
    }

    if (!success) {
      throw new Error('Failed to save chart event to both new and legacy endpoints');
    }

    return { persistedId, visitVersion };
  },

  async recordProcedureFromChart(
    visitId: number,
    patientId: string,
    payload: {
      procedure_type: string;
      tooth_no: string;
      visitVersion?: number;
    }
  ): Promise<void> {
    await apiPost('/api/doctor/procedures', {
      visit_id: visitId,
      patient_id: patientId,
      procedure_type: payload.procedure_type,
      tooth_no: payload.tooth_no,
      current_step: 0,
      step_no: 1,
      total_steps: 1, // Default for chart-based entries
      visit_version: payload.visitVersion
    });
  },

  async deleteChartEvent(
    eventId: string | number,
    expectedVersion?: number | null
  ): Promise<DeleteChartEventResult> {
    const eventIdText = String(eventId ?? '').trim();
    if (!eventIdText) return {};

    const query =
      expectedVersion != null && Number.isFinite(Number(expectedVersion))
        ? `?expected_version=${encodeURIComponent(String(expectedVersion))}`
        : '';

    try {
      const response = (await apiDelete(
        `/api/doctor/careflow360/events/${eventIdText}${query}`
      )) as unknown;
      const r = asRecord(response);
      return { visitVersion: parseVisitVersion(r?.stream_version) };
    } catch (error) {
      const e = error as Error & { status?: number };
      if (e.status === 409) throw error;
      const numericLegacyId = Number(eventIdText);
      if (Number.isFinite(numericLegacyId) && numericLegacyId > 0) {
        await apiPost(`/api/doctor/diagnoses/${numericLegacyId}/delete`, {}).catch((err) => {
          console.warn('Failed to delete legacy diagnosis', err);
        });
      }
      return {};
    }
  },

  async createToothHistory(
    visitId: number,
    payload: {
      tooth_number: string;
      label: string;
      note?: string;
      expected_version?: number | null;
    }
  ): Promise<{ historyId?: number; visitVersion?: number }> {
    const response = (await apiPost(`/api/doctor/careflow360/tooth-history/${visitId}`, {
      tooth_number: payload.tooth_number,
      label: payload.label,
      note: payload.note || '',
      expected_version: payload.expected_version ?? undefined,
    })) as unknown;
    const r = asRecord(response);
    return {
      historyId: Number.isFinite(Number(r?.id)) ? Number(r?.id) : undefined,
      visitVersion: parseVisitVersion(r?.stream_version),
    };
  },

  async deleteToothHistory(
    historyId: number,
    expectedVersion?: number | null
  ): Promise<{ visitVersion?: number }> {
    const query =
      expectedVersion != null && Number.isFinite(Number(expectedVersion))
        ? `?expected_version=${encodeURIComponent(String(expectedVersion))}`
        : '';
    const response = (await apiDelete(
      `/api/doctor/careflow360/tooth-history/${historyId}${query}`
    )) as unknown;
    const r = asRecord(response);
    return { visitVersion: parseVisitVersion(r?.stream_version) };
  },

  async fetchAuditTrail(visitId: number, limit = 40): Promise<CareflowAuditEntry[]> {
    try {
      const response = (await apiGet(
        `/api/doctor/careflow360/events/${visitId}/audit?limit=${encodeURIComponent(String(limit))}`
      )) as unknown;
      return Array.isArray(response) ? (response as CareflowAuditEntry[]) : [];
    } catch {
      return [];
    }
  },

  async fetchVisitTags(visitId: number): Promise<CareflowVisitTagsResponse> {
    try {
      const response = (await apiGet(`/api/doctor/careflow360/tags/${visitId}`)) as unknown;
      if (response && typeof response === 'object') {
        const typed = response as Partial<CareflowVisitTagsResponse>;
        return {
          visit_id: Number(typed.visit_id ?? visitId) || visitId,
          tags: Array.isArray(typed.tags)
            ? typed.tags.map((tag) => String(tag)).filter((tag) => tag.trim().length > 0)
            : [],
          updated_at: typed.updated_at ?? null,
        };
      }
    } catch {
      // noop
    }
    return { visit_id: visitId, tags: [], updated_at: null };
  },

  async saveVisitTags(
    visitId: number,
    tags: string[],
    ifUnmodifiedSince?: string | null
  ): Promise<CareflowVisitTagsResponse> {
    const response = (await apiPost(`/api/doctor/careflow360/tags/${visitId}`, {
      tags,
      if_unmodified_since: ifUnmodifiedSince || null,
    })) as unknown;
    if (response && typeof response === 'object') {
      const typed = response as Partial<CareflowVisitTagsResponse>;
      return {
        visit_id: Number(typed.visit_id ?? visitId) || visitId,
        tags: Array.isArray(typed.tags)
          ? typed.tags.map((tag) => String(tag)).filter((tag) => tag.trim().length > 0)
          : [],
        updated_at: typed.updated_at ?? null,
      };
    }
    return { visit_id: visitId, tags: [], updated_at: null };
  },

  async fetchCollaboration(
    visitId: number,
    includeAudit = true
  ): Promise<CareflowCollaborationBundle> {
    try {
      const response = (await apiGet(
        `/api/doctor/careflow360/collaboration/${visitId}?include_audit=${includeAudit ? '1' : '0'}`
      )) as unknown;
      const r = asRecord(response);
      if (!r) throw new Error('Invalid collaboration response');
      const notes = Array.isArray(r.notes)
        ? r.notes
            .map((item) => toCollaborationNote(item))
            .filter((item): item is CareflowCollaborationNote => item !== null)
        : [];
      const audit = Array.isArray(r.audit)
        ? r.audit
            .map((item) => toCollaborationAudit(item))
            .filter((item): item is CareflowCollaborationAuditEntry => item !== null)
        : [];
      return {
        visit_id: Number(r.visit_id ?? visitId) || visitId,
        notes,
        audit,
      };
    } catch {
      return { visit_id: visitId, notes: [], audit: [] };
    }
  },

  async createCollaborationNote(
    visitId: number,
    payload: { body: string; mentions?: string[]; attachment_ids?: number[] }
  ): Promise<CareflowCollaborationNote> {
    const response = (await apiPost(`/api/doctor/careflow360/collaboration/${visitId}`, {
      body: payload.body,
      mentions: payload.mentions || [],
      attachment_ids: payload.attachment_ids || [],
    })) as unknown;
    const note = toCollaborationNote(response);
    if (!note) throw new Error('Invalid collaboration note response');
    return note;
  },

  async updateCollaborationNote(
    noteId: number,
    payload: {
      body?: string;
      mentions?: string[];
      attachment_ids?: number[];
      expected_note_version?: number | null;
    }
  ): Promise<CareflowCollaborationNote> {
    const response = (await apiPut(`/api/doctor/careflow360/collaboration/note/${noteId}`, {
      body: payload.body,
      mentions: payload.mentions,
      attachment_ids: payload.attachment_ids,
      expected_note_version: payload.expected_note_version ?? undefined,
    })) as unknown;
    const note = toCollaborationNote(response);
    if (!note) throw new Error('Invalid collaboration update response');
    return note;
  },

  async deleteCollaborationNote(noteId: number): Promise<void> {
    await apiDelete(`/api/doctor/careflow360/collaboration/note/${noteId}`);
  },

  async deletePatient(patientId: string): Promise<void> {
    await apiDelete(`/api/patients/${encodeURIComponent(String(patientId))}`);
  },

  subscribeLive(
    visitId: number,
    handlers?: {
      onMessage?: (_event: CareflowLiveEvent) => void;
      onStatus?: (_status: CareflowLiveStatus) => void;
    }
  ): { close: () => void } {
    if (!Number.isFinite(visitId) || visitId <= 0) {
      handlers?.onStatus?.('error');
      return { close: () => undefined };
    }

    const token = getToken();
    if (!token) {
      handlers?.onStatus?.('unauthenticated');
      return { close: () => undefined };
    }

    let wsUrl = '';
    try {
      const httpUrl = toApiUrl(`/api/doctor/careflow360/live/${visitId}`);
      const resolved = new URL(httpUrl, window.location.origin);
      resolved.protocol = resolved.protocol === 'https:' ? 'wss:' : 'ws:';
      resolved.searchParams.set('token', token);
      wsUrl = resolved.toString();
    } catch (error) {
      console.error('Failed to resolve CareFlow websocket URL', error);
      handlers?.onStatus?.('error');
      return { close: () => undefined };
    }

    handlers?.onStatus?.('connecting');
    const socket = new WebSocket(wsUrl);
    let heartbeatTimer: number | null = null;

    const stopHeartbeat = () => {
      if (heartbeatTimer != null) {
        window.clearInterval(heartbeatTimer);
        heartbeatTimer = null;
      }
    };

    socket.onopen = () => {
      handlers?.onStatus?.('open');
      stopHeartbeat();
      heartbeatTimer = window.setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send('ping');
        }
      }, 25000);
    };

    socket.onmessage = (event) => {
      try {
        const parsed = JSON.parse(String(event.data || '{}')) as CareflowLiveEvent;
        if (parsed && typeof parsed === 'object') {
          handlers?.onMessage?.(parsed);
        }
      } catch {
        // Ignore malformed payloads.
      }
    };

    socket.onerror = () => {
      handlers?.onStatus?.('error');
    };

    socket.onclose = () => {
      stopHeartbeat();
      handlers?.onStatus?.('closed');
    };

    return {
      close: () => {
        stopHeartbeat();
        if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
          socket.close(1000, 'client_close');
        }
      },
    };
  },
};
